import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable } from 'rxjs';
import { DataService } from './data.service';
import { SubscriptionService } from './subscription.service';
import { LanguageService } from './language.service';

type LocalizedContentMap = Partial<Record<'en' | 'zh' | 'ja' | 'pt' | 'es', string>>;
type ConfigValue = string | number | boolean | null | ConfigValue[] | { [key: string]: ConfigValue };

export interface AppConfigPayload {
  environment: string;
  initialized: boolean;
  settings: Record<string, string>;
  lastUpdated?: string;
}

export interface FeatureFlags {
  anomalies: boolean;
  sessionTimeout: boolean;
  langZh: boolean;
  langJa: boolean;
  langPt: boolean;
  langEs: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private socket: Socket | null = null;
  private latestPayload: AppConfigPayload | null = null;
  private _config$ = new BehaviorSubject<{}[] | null>(null);
  private _initialized$ = new BehaviorSubject<boolean>(false);
  private _connectionState$ = new BehaviorSubject<string>('disconnected');
  private _releaseNotes$ = new BehaviorSubject<{}[] | null>(null);
  private _aiCapabilities$ = new BehaviorSubject<{}[] | null>(null); // Add this line
  private _featureFlags$ = new BehaviorSubject<FeatureFlags>({ anomalies: false, sessionTimeout: true, langZh: true, langJa: true, langPt: true, langEs: true });
  private _sessionTimeoutMinutes$ = new BehaviorSubject<number>(30);

  public config$ = this._config$.asObservable();
  public initialized$ = this._initialized$.asObservable();
  public connectionState$ = this._connectionState$.asObservable();
  public releaseNotes$ = this._releaseNotes$.asObservable();
  public aiCapabilities$ = this._aiCapabilities$.asObservable(); // Change this line
  public featureFlags$ = this._featureFlags$.asObservable();
  public sessionTimeoutMinutes$ = this._sessionTimeoutMinutes$.asObservable();

  constructor(
    private dataService: DataService,
    private subscriptionService: SubscriptionService,
    private languageService: LanguageService
  ) {
    this.languageService.currentLanguage$.subscribe(() => {
      if (this.latestPayload) {
        this.applyPayload(this.latestPayload, this._initialized$.value);
      }
    });

    this.connect();
  }

  defineChatProducts(config: AppConfigPayload) {
    const products = JSON.parse(config.settings["chatconfig_supported-systems"]);

    let productList: {}[] = [];
    for (let product of products) {
      productList.push(this.localizeConfigValue(JSON.parse(config.settings[product])) as {});
    }

    return productList;
  }

  defineReleaseNotes(config: AppConfigPayload) {
    const releases = JSON.parse(config.settings["global-release-notes"]);

    let releaseList: {}[] = [];
    for (let release of releases) {
      const keyPrefix = `release-notes-${release}`;
      const rawBaseNote = JSON.parse(config.settings[keyPrefix]) as Record<string, ConfigValue>;
      const baseNote = this.localizeConfigValue(rawBaseNote) as Record<string, ConfigValue>;
      const titleOverride = this.resolveExplicitConfigReference(config.settings, rawBaseNote['title']);
      const featureOverride = this.resolveReleaseNoteFieldValue(config.settings, rawBaseNote['features'], `${keyPrefix}-features`);
      const hasExplicitFeatureReference = this.isExplicitConfigReference(rawBaseNote['features']);

      if (typeof titleOverride === 'string' && titleOverride.trim().length > 0) {
        baseNote['title'] = titleOverride;
      }

      if (featureOverride !== null) {
        baseNote['features'] = this.normalizeReleaseNoteFeatures(featureOverride);
      } else if (hasExplicitFeatureReference) {
        console.warn(`Release note feature reference could not be resolved: ${rawBaseNote['features']}`);
        baseNote['features'] = [];
      } else if (baseNote['features'] !== undefined) {
        baseNote['features'] = this.normalizeReleaseNoteFeatures(baseNote['features']);
      }

      releaseList.push(baseNote as {});
    }

    return releaseList;
  }

  defineAiCapabilities(config: AppConfigPayload) {
    const aiVersions = JSON.parse(config.settings["global-ai-capabilities"]);

    let aiCapabilities: {}[] = [];
    for (let aiVersion of aiVersions) {
      const capability = this.resolveAiCapabilityValue(config.settings, aiVersion);
      if (capability !== null) {
        aiCapabilities.push(capability as {});
      }
    }

    return aiCapabilities;
  }

  getGlobalNotification(config: AppConfigPayload) {
    if (!config) return null;

    if (config.settings.hasOwnProperty('global-notification')) {
      const parsed = this.localizeConfigValue(JSON.parse(config.settings['global-notification'])) as Record<string, ConfigValue>;
      return parsed['active'] ? parsed : null;
    }

    return null;
  }

  private parseJsonIfPossible(value: string): ConfigValue {
    try {
      return JSON.parse(value) as ConfigValue;
    } catch (_) {
      return value;
    }
  }

  private isLocalizedContentMap(value: ConfigValue): value is LocalizedContentMap {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return false;
    }

    const keys = Object.keys(value);
    if (!keys.some((key) => ['en', 'zh', 'ja', 'pt', 'es'].includes(key))) {
      return false;
    }

    return keys.every((key) => {
      const entry = (value as Record<string, unknown>)[key];
      return entry == null || typeof entry === 'string';
    });
  }

  private resolveLocalizedContent(value: LocalizedContentMap): string {
    const activeLanguage = this.languageService.language.code;
    return value[activeLanguage] ?? value.en ?? Object.values(value).find((entry): entry is string => typeof entry === 'string') ?? '';
  }

  private resolveReleaseNoteFieldValue(
    settings: Record<string, string>,
    fieldValue: ConfigValue | undefined,
    fallbackKeyPrefix: string
  ): ConfigValue | null {
    const inlineReferenceValue = this.resolveExplicitConfigReference(settings, fieldValue);
    if (inlineReferenceValue !== null) {
      return inlineReferenceValue;
    }

    return this.resolveSplitConfigValue(settings, fallbackKeyPrefix);
  }

  private resolveExplicitConfigReference(settings: Record<string, string>, fieldValue: ConfigValue | undefined): ConfigValue | null {
    if (!this.isExplicitConfigReference(fieldValue)) {
      return null;
    }

    const candidate = fieldValue.trim();
    return this.resolveSplitConfigValue(settings, candidate);
  }

  private isExplicitConfigReference(fieldValue: ConfigValue | undefined): fieldValue is string {
    return typeof fieldValue === 'string' && fieldValue.trim().startsWith('release-notes-');
  }

  private resolveAiCapabilityValue(settings: Record<string, string>, aiVersion: ConfigValue): ConfigValue | null {
    if (typeof aiVersion === 'string' && aiVersion.trim().length > 0) {
      const normalizedVersion = aiVersion.trim();
      const directCapability = this.resolveSplitConfigValue(settings, normalizedVersion);
      if (directCapability !== null) {
        return directCapability;
      }

      if (normalizedVersion.startsWith('ai-capabilities-')) {
        return null;
      }

      return this.resolveSplitConfigValue(settings, `ai-capabilities-${normalizedVersion}`);
    }

    if (aiVersion === null || aiVersion === undefined) {
      return null;
    }

    return this.localizeConfigValue(aiVersion);
  }

  private getSettingValue(settings: Record<string, string>, key: string): string | undefined {
    if (settings[key] !== undefined) {
      return settings[key];
    }

    const matchedKey = Object.keys(settings).find((candidate) => candidate.toLowerCase() === key.toLowerCase());
    return matchedKey ? settings[matchedKey] : undefined;
  }

  private resolveSplitConfigValue(settings: Record<string, string>, keyPrefix: string): ConfigValue | null {
    const directValue = this.getSettingValue(settings, keyPrefix);
    if (directValue !== undefined) {
      return this.localizeConfigValue(this.parseJsonIfPossible(directValue));
    }

    const activeLanguage = this.languageService.language.code;
    const fallbackKeys = [`${keyPrefix}-${activeLanguage}`, `${keyPrefix}-en`];
    for (const key of fallbackKeys) {
      const value = this.getSettingValue(settings, key);
      if (value !== undefined) {
        return this.parseJsonIfPossible(value);
      }
    }

    for (const lang of ['en', 'zh', 'ja', 'pt', 'es']) {
      const key = `${keyPrefix}-${lang}`;
      const value = this.getSettingValue(settings, key);
      if (value !== undefined) {
        return this.parseJsonIfPossible(value);
      }
    }

    return null;
  }

  private normalizeReleaseNoteFeatures(value: ConfigValue): string[] {
    if (Array.isArray(value)) {
      return value
        .map((entry) => typeof entry === 'string' ? entry.trim() : '')
        .filter((entry) => entry.length > 0);
    }

    if (typeof value !== 'string') {
      return [];
    }

    const normalized = value.replace(/\r\n/g, '\n').trim();
    if (!normalized) {
      return [];
    }

    if (!normalized.includes('\n- ') && !normalized.startsWith('- ')) {
      return [normalized];
    }

    return normalized
      .split(/(?:^|\n)-\s+/)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }

  private localizeConfigValue(value: ConfigValue): ConfigValue {
    if (Array.isArray(value)) {
      return value.map((entry) => this.localizeConfigValue(entry));
    }

    if (this.isLocalizedContentMap(value)) {
      return this.resolveLocalizedContent(value);
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, entry]) => [key, this.localizeConfigValue(entry as ConfigValue)])
      );
    }

    return value;
  }

  parseSessionTimeoutMinutes(config: AppConfigPayload): number {
    try {
      const raw = config.settings['session-timeout-minutes'];
      if (raw) {
        const minutes = parseInt(raw, 10);
        if (!isNaN(minutes) && minutes > 0) return minutes;
      }
    } catch (_) {}
    return 30;
  }

  parseFeatureFlags(config: AppConfigPayload): FeatureFlags {
    let anomalies = false;
    let sessionTimeout = true;
    let langZh = true;
    let langJa = true;
    let langPt = true;
    let langEs = true;
    try {
      const raw = config.settings['.appconfig.featureflag/anomalies'];
      if (raw) {
        anomalies = JSON.parse(raw).enabled === true;
      }
    } catch (_) {}
    try {
      const raw = config.settings['.appconfig.featureflag/sessionTimeout'];
      if (raw) { sessionTimeout = JSON.parse(raw).enabled === true; }
    } catch (_) {}
    try {
      const raw = config.settings['.appconfig.featureflag/langZh'];
      if (raw) { langZh = JSON.parse(raw).enabled === true; }
    } catch (_) {}
    try {
      const raw = config.settings['.appconfig.featureflag/langJa'];
      if (raw) { langJa = JSON.parse(raw).enabled === true; }
    } catch (_) {}
    try {
      const raw = config.settings['.appconfig.featureflag/langPt'];
      if (raw) { langPt = JSON.parse(raw).enabled === true; }
    } catch (_) {}
    try {
      const raw = config.settings['.appconfig.featureflag/langEs'];
      if (raw) { langEs = JSON.parse(raw).enabled === true; }
    } catch (_) {}
    return { anomalies, sessionTimeout, langZh, langJa, langPt, langEs };
  }

  private handleGlobalNotification(config: AppConfigPayload): void {
    const globalNotification = this.getGlobalNotification(config);
    const current = this.subscriptionService.$notifications.getValue();
    const currentCenter = this.subscriptionService.$notificationsCenter.getValue();

    const removeGlobalNotification = (notifications: any[]) => notifications.filter(
      (notification) => notification.id !== 'global-notification'
    );

    if (!globalNotification) {
      this.subscriptionService.$notifications.next(removeGlobalNotification(current));
      this.subscriptionService.$notificationsCenter.next(removeGlobalNotification(currentCenter));
      return;
    }

    const existing = currentCenter.find((notification) => notification.id === 'global-notification');

    const entry = {
      id: 'global-notification',
      type: globalNotification['type'],
      title: globalNotification['label'],
      message: globalNotification['message'],
      timestamp: existing?.timestamp ?? new Date().toISOString(),
      read: existing?.read ?? false
    };

    const upsert = (notifications: any[]) => {
      const next = [...removeGlobalNotification(notifications)];
      next.push(entry);
      return next;
    };

    this.subscriptionService.$notifications.next(upsert(current));
    this.subscriptionService.$notificationsCenter.next(upsert(currentCenter));
  }

  private applyPayload(payload: AppConfigPayload, markInitialized: boolean): void {
    this.latestPayload = payload;

    const chatProducts = this.defineChatProducts(payload);
    const releaseNotes = this.defineReleaseNotes(payload);
    const aiCapabilities = this.defineAiCapabilities(payload);
    const featureFlags = this.parseFeatureFlags(payload);
    const sessionTimeoutMinutes = this.parseSessionTimeoutMinutes(payload);

    this._config$.next(chatProducts);
    this._releaseNotes$.next(releaseNotes);
    this._aiCapabilities$.next(aiCapabilities);
    this._featureFlags$.next(featureFlags);
    this._sessionTimeoutMinutes$.next(sessionTimeoutMinutes);

    if (markInitialized) {
      this._initialized$.next(true);
    }

    this.handleGlobalNotification(payload);
  }

  public connect() {
    if (this.socket) return; // already connected/connecting
    this._connectionState$.next('connecting');
    
    // Assumes same origin. Adjust if API served elsewhere.
    this.socket = io(window.location.protocol + "//" + window.location.hostname + (window.location.port !== "" ? ":" + "8080" : "") + "/config", {
      path: '/socket.io',
      transports: ['websocket', 'polling'], // fallback allowed
      reconnectionAttempts: 10,
      reconnectionDelay: 2000
    });

    this.socket.on('connect', () => {
      console.log('[AppConfig] Socket connected');
      this._connectionState$.next('connected');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[AppConfig] Socket disconnected:', reason);
      this._connectionState$.next('disconnected');
    });

    // Initial full snapshot
    this.socket.on('appconfig-snapshot', (payload: AppConfigPayload) => {
      console.log('[AppConfig] Received snapshot');
      this.applyPayload(payload, true);
    });

    // Subsequent updates
    this.socket.on('appconfig-update', (payload: AppConfigPayload) => {
      console.log('[AppConfig] Received update');
      this.applyPayload(payload, this._initialized$.value);
    });

    this.socket.on('connect_error', (err) => {
      console.error('[AppConfig] Socket connection error:', err.message);
      this._connectionState$.next('error');
    });
  }

  // Updated getter with safety check
  get current() { 
    const value = this._config$.value;
    const isInitialized = this._initialized$.value;
    
    if (!isInitialized) {
      console.warn('[AppConfig] Config accessed before initialization complete');
    }
    
    return value; 
  }

  // Helper method to wait for initialization
  waitForInitialization(): Promise<{}[] | null> {
    return new Promise((resolve) => {
      if (this._initialized$.value) {
        resolve(this._config$.value);
      } else {
        const subscription = this.initialized$.subscribe((initialized) => {
          if (initialized) {
            subscription.unsubscribe();
            resolve(this._config$.value);
          }
        });
      }
    });
  }

  // Helper method to get current connection state
  get connectionStatus(): string {
    return this._connectionState$.value;
  }

  // Method to disconnect socket
  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this._connectionState$.next('disconnected');
    }
  }
}
